import { useState } from "react";
import { Link } from "react-router-dom";
import { SupportDialog } from "@/components/support/support-dialog";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Plus, User, Loader2, Flame, Mail, Sparkles, GraduationCap, LifeBuoy, LogIn,
  MoreHorizontal, UserRound, Pencil, PlayCircle, FileText,
} from "lucide-react";
import { toast } from "sonner";

import { GET_STUDENTS } from "@/graphql/queries/students";
import { ENTER_KID_MODE } from "@/graphql/mutations/kid-mode";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/AuthContext";
import { ageLabel, gradeLabel } from "@/lib/students";

export default function StudentsListPage() {
  const { data, loading } = useQuery(GET_STUDENTS);
  const students = data?.students ?? [];
  const { impersonationEnabled } = useAuth();
  const [handingOver, setHandingOver] = useState<string | null>(null);
  const [enterKidMode] = useMutation(ENTER_KID_MODE);

  // One click, no confirmation — going in is meant to be effortless. Coming back
  // is the part that asks for a PIN.
  const handOver = async (studentId: string) => {
    if (handingOver) return;
    setHandingOver(studentId);

    try {
      const { data: result } = await enterKidMode({ variables: { studentId } });
      const payload = (result as any)?.enterKidMode;

      if (payload?.success) {
        // The auth cookie was swapped server-side; reload so no grown-up data
        // survives in the Apollo cache into the learner's shell.
        window.location.assign("/");
        return;
      }

      toast.error(payload?.errors?.[0] ?? "Nepavyko perjungti");
    } catch {
      toast.error("Nepavyko perjungti");
    } finally {
      setHandingOver(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl">
            Mokiniai
          </h1>
          <p className="mt-1 text-muted-foreground">
            Valdykite profilius ir stebėkite pažangą
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button size="lg" className="rounded-full shadow-md" asChild>
            <Link to="/students/new">
              <Plus className="mr-2 h-4 w-4" />
              Naujas mokinys
            </Link>
          </Button>
          {/* No learner to name from here, so this is the same door the sidebar
              has — worth repeating because on a phone the sidebar is a sheet
              nobody opens by accident, and this is the screen a grown-up is on
              when „kaip čia pridėti antrą tėvą" occurs to them. */}
          <SupportDialog
            trigger={
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <LifeBuoy className="mr-1.5 h-3.5 w-3.5" />
                Reikia pagalbos?
              </Button>
            }
          />
        </div>
      </div>

      {students.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="mb-4 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 p-5">
              <User className="h-12 w-12 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium">Nėra mokinių</p>
            <p className="mb-6 text-sm text-muted-foreground">
              Pridėkite pirmąjį mokinį, kad pradėtumėte
            </p>
            <Button size="lg" className="rounded-full" asChild>
              <Link to="/students/new">
                <Plus className="mr-2 h-4 w-4" />
                Pridėti mokinį
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {students.map((student: any) => {
            const initials = student.name
              .split(" ")
              .map((n: string) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2);

            return (
              <Link
                key={student.id}
                to={`/students/${student.id}`}
                className="group relative block overflow-clip rounded-3xl border bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-amber-500/5 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/10 dark:from-purple-500/10 dark:via-pink-500/10 dark:to-amber-500/10"
              >
                {/* Everything else a grown-up comes to this card for, without
                    crowding it: the person (profile, edit), then their learning.
                    Deleting stays on the edit page — a destructive action does
                    not belong one tap away on a list. The card is itself a link,
                    so the trigger and the menu stop the click reaching it. */}
                <div
                  className="absolute right-3 top-3 z-10"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        title="Daugiau veiksmų"
                        aria-label={`Daugiau veiksmų: ${student.name}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/70 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-background hover:text-foreground cursor-pointer"
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52" onClick={(event) => event.stopPropagation()}>
                      <DropdownMenuItem asChild>
                        <Link to={`/students/${student.id}`}>
                          <UserRound className="mr-2 h-4 w-4" />
                          Profilis
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/students/${student.id}/edit`}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Redaguoti
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to={`/sessions/new?studentId=${student.id}`}>
                          <PlayCircle className="mr-2 h-4 w-4" />
                          Nauja sesija
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/students/${student.id}/skills`}>
                          <GraduationCap className="mr-2 h-4 w-4" />
                          Įgūdžiai
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/students/${student.id}/reports`}>
                          <FileText className="mr-2 h-4 w-4" />
                          Ataskaitos
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Decorative glow on hover */}
                <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-purple-400/20 to-pink-400/20 blur-2xl transition-opacity group-hover:opacity-100 opacity-0" />

                {/* Avatar section — the hero of the card */}
                <div className="relative flex items-center justify-center px-6 pt-8 pb-4">
                  <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-purple-500/10 via-pink-500/5 to-transparent" />
                  <div className="relative">
                    <div className="absolute inset-0 -z-10 scale-110 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 opacity-20 blur-xl transition-opacity group-hover:opacity-40" />
                    <div className="rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 p-1 shadow-lg transition-transform group-hover:scale-105">
                      <Avatar className="h-36 w-36 border-4 border-background">
                        {student.avatarUrl && (
                          <AvatarImage
                            src={student.avatarUrl}
                            alt={student.name}
                            className="object-cover"
                          />
                        )}
                        <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-3xl text-white">
                          {initials || <User className="h-12 w-12" />}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    {student.level?.icon && (
                      <div className="absolute -bottom-1 -right-1 flex h-10 w-10 items-center justify-center rounded-full border-4 border-background bg-background text-xl shadow-md">
                        {student.level.icon}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info section */}
                <div className="relative space-y-3 px-6 pb-6 text-center">
                  <div>
                    <h3 className="truncate text-xl font-bold tracking-tight">
                      {student.name}
                    </h3>
                    {/* Joined rather than each part carrying its own separator:
                        the age and the grade are both unknown until somebody says
                        when this learner was born, and either may be the first. */}
                    <p className="text-sm text-muted-foreground">
                      {[
                        student.age != null ? ageLabel(student.age) : null,
                        student.grade != null ? gradeLabel(student.grade) : null,
                        student.level?.name,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {/* A learner nobody has set up looks identical to one who
                        is simply new, and the difference decides whether their
                        sessions mean anything. */}
                    {!student.skillSetupDone && (
                      <Link
                        to={`/students/${student.id}/skills`}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                      >
                        <GraduationCap className="h-3 w-3" />
                        Įgūdžiai nenustatyti
                      </Link>
                    )}
                    {/* They are in no family and cannot sign in until they follow
                        the link, so this row is not the same as the others — and
                        the grown-up who added them is the only person who can
                        chase it up. Not a link: there is nothing on any screen to
                        press, the next move is in somebody's inbox. */}
                    {student.emailPending && (
                      <span
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                        title="Išsiuntėme patvirtinimo nuorodą. Kol jos nepaspaus, mokinys neprisijungs ir šeimoje nebus."
                      >
                        <Mail className="h-3 w-3" />
                        El. paštas nepatvirtintas
                      </span>
                    )}
                  </div>

                  {/* Stats pills */}
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    <div className="inline-flex items-center gap-1 rounded-full border bg-background/70 px-2.5 py-1 text-xs backdrop-blur-sm">
                      <span>💎</span>
                      <span className="font-semibold">
                        {student.totalPoints?.toLocaleString() ?? 0}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-full border bg-background/70 px-2.5 py-1 text-xs backdrop-blur-sm">
                      <Flame className="h-3 w-3 text-orange-500" />
                      <span className="font-semibold">
                        {student.currentStreak ?? 0}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-full border bg-background/70 px-2.5 py-1 text-xs backdrop-blur-sm">
                      <Sparkles className="h-3 w-3 text-purple-500" />
                      <span className="font-semibold">
                        {student.sessionCount ?? 0}
                      </span>
                      <span className="text-muted-foreground">sesijos</span>
                    </div>
                  </div>

                  {student.notes && (
                    <p className="line-clamp-2 pt-1 text-xs text-muted-foreground">
                      {student.notes}
                    </p>
                  )}

                  {/* „Mokytis" alone read as a link to some learning page. What
                      it does is switch this device into the learner's own
                      account, so the button says whose, and the line under it
                      says how to get back. The name stays in the nominative after
                      „kaip" — declining arbitrary names is not something to
                      guess at. */}
                  {impersonationEnabled && (
                    <div className="space-y-1.5">
                      <Button
                        className="w-full rounded-full"
                        title="Šiame įrenginyje atsidarys mokinio paskyra. Grįžti galėsite įvedę tėvų PIN kodą."
                        disabled={handingOver !== null}
                        onClick={(event) => {
                          // The whole card is a link to the learner's profile;
                          // this button means something else entirely.
                          event.preventDefault();
                          event.stopPropagation();
                          handOver(student.id);
                        }}
                      >
                        {handingOver === student.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <LogIn className="mr-2 h-4 w-4" />
                        )}
                        <span className="truncate">Mokytis kaip {student.name.split(" ")[0]}</span>
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        Perjungs į mokinio paskyrą · grįžti – su PIN kodu
                      </p>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
